import { useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  CareEnrollmentPaymentInfoFragment,
  CarePlanInfoFragment,
  CarePlanPricingInfoFragment,
  StripeDisputeStatus,
  useGetCareEnrollmentInfoQuery,
} from 'shared/types/graphql';

interface UseCareEnrollmentInfoInput {
  enrollmentId: string;
}

interface UseCareEnrollmentInfoOutput {
  hasPlanDiscount: boolean;
  planDiscount: number;
  plan?: CarePlanInfoFragment;
  planPricing?: CarePlanPricingInfoFragment;
  validPayments: CareEnrollmentPaymentInfoFragment[];
  disputedPaymentCount: number;
  disputedPayments: CareEnrollmentPaymentInfoFragment[];
  refundedPaymentCount: number;
  refundedPayments: CareEnrollmentPaymentInfoFragment[];
  startDate: string | Date;
  isLoading: boolean;
}

export const useCareEnrollmentInfo = ({ enrollmentId }: UseCareEnrollmentInfoInput): UseCareEnrollmentInfoOutput => {
  const { data, loading: isLoading } = useGetCareEnrollmentInfoQuery({
    variables: {
      enrollmentId,
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const enrollmentInfo = data?.findUniqueCarePlanEnrollment;
  const { plan, planPricing } = enrollmentInfo || {};
  const hasPlanDiscount = !!planPricing?.planDiscount;
  const planDiscount = planPricing?.planDiscount ? planPricing?.planDiscount / 100 : 0;

  const validPayments = useMemo(() => {
    return (
      enrollmentInfo?.enrollmentPayments.filter(
        (payment) =>
          !payment.stripeDispute ||
          payment.stripeDispute.status === StripeDisputeStatus.Won ||
          payment.stripeDispute.status === StripeDisputeStatus.WarningClosed,
      ) || []
    );
  }, [enrollmentInfo?.enrollmentPayments]);

  const { refundedPayments, refundedPaymentCount } = useMemo(() => {
    const payments = enrollmentInfo?.enrollmentPayments.filter((payment) => !!payment.stripeRefund) || [];
    return { refundedPayments: payments, refundedPaymentCount: payments.length };
  }, [enrollmentInfo?.enrollmentPayments]);

  const { disputedPayments, disputedPaymentCount } = useMemo(() => {
    const payments =
      enrollmentInfo?.enrollmentPayments.filter(
        (payment) =>
          payment.stripeDispute &&
          payment.stripeDispute.status !== StripeDisputeStatus.Won &&
          payment.stripeDispute.status !== StripeDisputeStatus.WarningClosed,
      ) || [];
    return { disputedPayments: payments, disputedPaymentCount: payments.length };
  }, [enrollmentInfo?.enrollmentPayments]);

  return {
    hasPlanDiscount,
    planDiscount,
    plan,
    planPricing: planPricing || undefined,
    validPayments,
    disputedPaymentCount,
    disputedPayments,
    refundedPaymentCount,
    refundedPayments,
    isLoading,
    startDate: enrollmentInfo?.startDate,
  };
};
